import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { m } from "framer-motion";
import { supabase } from "../../lib/supabase";
import { useStoreSettings } from "../../contexts/StoreSettingsContext";
import { useViewportAnimationTrigger } from "../../hooks/useViewportAnimationTrigger";
import { pageFade } from "../../components/animations/menuAnimations";
import { logger } from "../../utils/logger";
import ConfirmationModal from "../../components/ui/ConfirmationModal";

interface FormData {
  enable_loyalty_program: boolean;
  enable_reservations: boolean;
  enable_menu_filters: boolean;
  enable_product_customization: boolean;
  enable_order_tracking: boolean;
  enable_order_feedback: boolean;
  enable_marketing_optins: boolean;
  enable_quick_reorder: boolean;
}

function AdminFeatureFlags(): JSX.Element {
  const navigate = useNavigate();
  const containerRef = useViewportAnimationTrigger();
  const {
    settings,
    loading: contextLoading,
    updateSettings,
  } = useStoreSettings();
  const [isAdmin, setIsAdmin] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState<FormData>({
    enable_loyalty_program: false,
    enable_reservations: true,
    enable_menu_filters: false,
    enable_product_customization: false,
    enable_order_tracking: false,
    enable_order_feedback: false,
    enable_marketing_optins: false,
    enable_quick_reorder: false,
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const isInitialLoadRef = useRef(true);

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async (): Promise<void> => {
      setVerifying(true);
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) {
          setError("Log in to access admin tools.");
          setIsAdmin(false);
          navigate("/login");
          return;
        }

        const { data, error: customerError } = await supabase
          .from("customers")
          .select("is_admin")
          .eq("id", user.id)
          .single();

        const customerData = data as { is_admin?: boolean } | null;
        if (customerError || !customerData?.is_admin) {
          setError("Access denied. Administrator role required.");
          setIsAdmin(false);
          navigate("/admin");
          return;
        }

        setIsAdmin(true);
        setError("");
      } catch (err) {
        logger.error(err);
        setError("Unable to verify admin permissions.");
        setIsAdmin(false);
        navigate("/admin");
      } finally {
        setVerifying(false);
      }
    };

    checkAdminStatus();
  }, [navigate]);

  // Load settings into form when they're available - ONLY ON INITIAL LOAD
  useEffect(() => {
    if (settings && isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      setFormData({
        enable_loyalty_program: settings.enable_loyalty_program ?? true,
        enable_reservations: settings.enable_reservations ?? true,
        enable_menu_filters: settings.enable_menu_filters ?? true,
        enable_product_customization:
          settings.enable_product_customization ?? true,
        enable_order_tracking: settings.enable_order_tracking ?? true,
        enable_order_feedback: settings.enable_order_feedback ?? true,
        enable_marketing_optins: settings.enable_marketing_optins ?? true,
        enable_quick_reorder: settings.enable_quick_reorder ?? true,
      });
    }
  }, [settings]);

  const handleToggle = (field: keyof FormData): void => {
    const nextValue = !formData[field];
    setFormData((prev) => ({
      ...prev,
      [field]: nextValue,
    }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setSaveError("");
    setSuccess(false);

    try {
      const updates: Record<string, unknown> = {
        enable_loyalty_program: formData.enable_loyalty_program,
        enable_reservations: formData.enable_reservations,
        enable_menu_filters: formData.enable_menu_filters,
        enable_product_customization: formData.enable_product_customization,
        enable_order_tracking: formData.enable_order_tracking,
        enable_order_feedback: formData.enable_order_feedback,
        enable_marketing_optins: formData.enable_marketing_optins,
        enable_quick_reorder: formData.enable_quick_reorder,
      };

      const result = await updateSettings(updates);

      if (result.success) {
        setSuccess(true);
        setHasUnsavedChanges(false);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setSaveError(result.error || "Failed to save settings");
      }
    } catch (err) {
      logger.error("Error saving settings:", err);
      setSaveError(
        err instanceof Error ? err.message : "Failed to save settings",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = (): void => {
    if (settings) {
      setFormData({
        enable_loyalty_program: settings.enable_loyalty_program ?? true,
        enable_reservations: settings.enable_reservations ?? true,
        enable_menu_filters: settings.enable_menu_filters ?? true,
        enable_product_customization:
          settings.enable_product_customization ?? true,
        enable_order_tracking: settings.enable_order_tracking ?? true,
        enable_order_feedback: settings.enable_order_feedback ?? true,
        enable_marketing_optins: settings.enable_marketing_optins ?? true,
        enable_quick_reorder: settings.enable_quick_reorder ?? true,
      });
      setHasUnsavedChanges(false);
      setSaveError("");
      setSuccess(false);
    }
  };

  if (verifying || contextLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[var(--accent)] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-muted">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-lg rounded-2xl border border-red-500/30 bg-red-500/10 p-8 text-center shadow-[0_25px_60px_-45px_rgba(248,113,113,0.6)]">
        <h2 className="text-2xl font-semibold mb-2 text-[var(--text-main)]">
          Admin Access Required
        </h2>
        <p className="text-sm text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <m.main
      ref={containerRef}
      className="w-full bg-[var(--bg-main)] text-[var(--text-main)]"
      variants={pageFade}
      initial="hidden"
      animate="visible"
      exit="exit"
      style={{
        pointerEvents: "auto",
        // Add padding to match .app-container spacing (prevents sections from touching viewport edges)
        paddingLeft: "clamp(1rem, 3vw, 3.5rem)",
        paddingRight: "clamp(1rem, 3vw, 3.5rem)",
        // Ensure no overflow constraints that break positioning
        overflow: "visible",
        overflowX: "visible",
        overflowY: "visible",
      }}
    >
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-3 sm:py-4 md:py-5">
        <header
          className="mb-12 flex flex-col gap-3 sm:gap-4 md:gap-6 md:flex-row md:items-end md:justify-between"
          data-animate="fade-rise"
          data-animate-active="false"
        >
          <div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-[var(--text-main)]">
              Feature Flags
            </h1>
            <p className="mt-2 text-sm sm:text-base text-muted">
              Control which features are visible to customers across your store
            </p>
          </div>
          <button
            onClick={() => navigate("/admin/settings")}
            className="px-4 py-2 rounded-xl border border-theme text-muted hover:text-[var(--text-main)] hover:border-[var(--accent)] transition-all duration-200"
          >
            Back to Settings
          </button>
        </header>

        {/* Feature Flags Section */}
        <div
          data-animate="fade-scale"
          data-animate-active="false"
          className="bg-theme-elevated rounded-xl sm:rounded-2xl shadow-sm p-4 sm:p-6 md:p-10 border border-theme text-[var(--text-main)]"
        >
          <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-[var(--text-main)] mb-2">
            Feature Flags
          </h2>
          <p className="text-sm sm:text-base text-[var(--text-muted)] mb-6">
            Control which features are visible to customers
          </p>

          <div className="space-y-6">
            {/* Loyalty Program */}
            <div className="flex items-start justify-between gap-4">
              <div className="max-w-md">
                <p className="text-sm font-medium text-[var(--text-main)] mb-1">
                  Star Rewards (Loyalty Program)
                </p>
                <p className="text-xs text-[var(--text-muted)]">
                  Enable the loyalty program with points, tiers, and referral
                  system
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.enable_loyalty_program}
                  aria-label="Toggle loyalty program"
                  onClick={() => handleToggle("enable_loyalty_program")}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full border transition-all duration-200 ${
                    formData.enable_loyalty_program
                      ? "bg-[#C59D5F] border-[#E5C990] shadow-[0_0_12px_rgba(197,157,95,0.45)]"
                      : "bg-theme-elevated border-transparent"
                  } hover:scale-[1.02]`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-all duration-200 ${
                      formData.enable_loyalty_program
                        ? "translate-x-6 shadow-[0_4px_14px_rgba(197,157,95,0.35)]"
                        : "translate-x-1"
                    }`}
                  />
                </button>
                <span className="text-[0.65rem] uppercase tracking-[0.25em] text-[var(--text-muted)]">
                  {formData.enable_loyalty_program ? "ON" : "OFF"}
                </span>
              </div>
            </div>

            {/* Reservations */}
            <div className="flex items-start justify-between gap-4">
              <div className="max-w-md">
                <p className="text-sm font-medium text-[var(--text-main)] mb-1">
                  Table Reservations
                </p>
                <p className="text-xs text-[var(--text-muted)]">
                  Enable table reservation system with booking form
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.enable_reservations}
                  aria-label="Toggle reservations"
                  onClick={() => handleToggle("enable_reservations")}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full border transition-all duration-200 ${
                    formData.enable_reservations
                      ? "bg-[#C59D5F] border-[#E5C990] shadow-[0_0_12px_rgba(197,157,95,0.45)]"
                      : "bg-theme-elevated border-transparent"
                  } hover:scale-[1.02]`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-all duration-200 ${
                      formData.enable_reservations
                        ? "translate-x-6 shadow-[0_4px_14px_rgba(197,157,95,0.35)]"
                        : "translate-x-1"
                    }`}
                  />
                </button>
                <span className="text-[0.65rem] uppercase tracking-[0.25em] text-[var(--text-muted)]">
                  {formData.enable_reservations ? "ON" : "OFF"}
                </span>
              </div>
            </div>

            {/* Menu Filters */}
            <div className="flex items-start justify-between gap-4">
              <div className="max-w-md">
                <p className="text-sm font-medium text-[var(--text-main)] mb-1">
                  Menu Filters
                </p>
                <p className="text-xs text-[var(--text-muted)]">
                  Enable dietary and allergen filters on menu page
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.enable_menu_filters}
                  aria-label="Toggle menu filters"
                  onClick={() => handleToggle("enable_menu_filters")}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full border transition-all duration-200 ${
                    formData.enable_menu_filters
                      ? "bg-[#C59D5F] border-[#E5C990] shadow-[0_0_12px_rgba(197,157,95,0.45)]"
                      : "bg-theme-elevated border-transparent"
                  } hover:scale-[1.02]`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-all duration-200 ${
                      formData.enable_menu_filters
                        ? "translate-x-6 shadow-[0_4px_14px_rgba(197,157,95,0.35)]"
                        : "translate-x-1"
                    }`}
                  />
                </button>
                <span className="text-[0.65rem] uppercase tracking-[0.25em] text-[var(--text-muted)]">
                  {formData.enable_menu_filters ? "ON" : "OFF"}
                </span>
              </div>
            </div>

            {/* Product Customization */}
            <div className="flex items-start justify-between gap-4">
              <div className="max-w-md">
                <p className="text-sm font-medium text-[var(--text-main)] mb-1">
                  Product Customization
                </p>
                <p className="text-xs text-[var(--text-muted)]">
                  Enable add-ons and spice level customization on products
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.enable_product_customization}
                  aria-label="Toggle product customization"
                  onClick={() => handleToggle("enable_product_customization")}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full border transition-all duration-200 ${
                    formData.enable_product_customization
                      ? "bg-[#C59D5F] border-[#E5C990] shadow-[0_0_12px_rgba(197,157,95,0.45)]"
                      : "bg-theme-elevated border-transparent"
                  } hover:scale-[1.02]`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-all duration-200 ${
                      formData.enable_product_customization
                        ? "translate-x-6 shadow-[0_4px_14px_rgba(197,157,95,0.35)]"
                        : "translate-x-1"
                    }`}
                  />
                </button>
                <span className="text-[0.65rem] uppercase tracking-[0.25em] text-[var(--text-muted)]">
                  {formData.enable_product_customization ? "ON" : "OFF"}
                </span>
              </div>
            </div>

            {/* Order Tracking */}
            <div className="flex items-start justify-between gap-4">
              <div className="max-w-md">
                <p className="text-sm font-medium text-[var(--text-main)] mb-1">
                  Order Tracking
                </p>
                <p className="text-xs text-[var(--text-muted)]">
                  Enable live order tracking timeline
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.enable_order_tracking}
                  aria-label="Toggle order tracking"
                  onClick={() => handleToggle("enable_order_tracking")}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full border transition-all duration-200 ${
                    formData.enable_order_tracking
                      ? "bg-[#C59D5F] border-[#E5C990] shadow-[0_0_12px_rgba(197,157,95,0.45)]"
                      : "bg-theme-elevated border-transparent"
                  } hover:scale-[1.02]`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-all duration-200 ${
                      formData.enable_order_tracking
                        ? "translate-x-6 shadow-[0_4px_14px_rgba(197,157,95,0.35)]"
                        : "translate-x-1"
                    }`}
                  />
                </button>
                <span className="text-[0.65rem] uppercase tracking-[0.25em] text-[var(--text-muted)]">
                  {formData.enable_order_tracking ? "ON" : "OFF"}
                </span>
              </div>
            </div>

            {/* Order Feedback */}
            <div className="flex items-start justify-between gap-4">
              <div className="max-w-md">
                <p className="text-sm font-medium text-[var(--text-main)] mb-1">
                  Order Feedback
                </p>
                <p className="text-xs text-[var(--text-muted)]">
                  Enable post-meal feedback and rating system
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.enable_order_feedback}
                  aria-label="Toggle order feedback"
                  onClick={() => handleToggle("enable_order_feedback")}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full border transition-all duration-200 ${
                    formData.enable_order_feedback
                      ? "bg-[#C59D5F] border-[#E5C990] shadow-[0_0_12px_rgba(197,157,95,0.45)]"
                      : "bg-theme-elevated border-transparent"
                  } hover:scale-[1.02]`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-all duration-200 ${
                      formData.enable_order_feedback
                        ? "translate-x-6 shadow-[0_4px_14px_rgba(197,157,95,0.35)]"
                        : "translate-x-1"
                    }`}
                  />
                </button>
                <span className="text-[0.65rem] uppercase tracking-[0.25em] text-[var(--text-muted)]">
                  {formData.enable_order_feedback ? "ON" : "OFF"}
                </span>
              </div>
            </div>

            {/* Quick Reorder */}
            <div className="flex items-start justify-between gap-4">
              <div className="max-w-md">
                <p className="text-sm font-medium text-[var(--text-main)] mb-1">
                  Quick Reorder
                </p>
                <p className="text-xs text-[var(--text-muted)]">
                  Enable quick reorder functionality for previous orders
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.enable_quick_reorder}
                  aria-label="Toggle quick reorder"
                  onClick={() => handleToggle("enable_quick_reorder")}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full border transition-all duration-200 ${
                    formData.enable_quick_reorder
                      ? "bg-[#C59D5F] border-[#E5C990] shadow-[0_0_12px_rgba(197,157,95,0.45)]"
                      : "bg-theme-elevated border-transparent"
                  } hover:scale-[1.02]`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-all duration-200 ${
                      formData.enable_quick_reorder
                        ? "translate-x-6 shadow-[0_4px_14px_rgba(197,157,95,0.35)]"
                        : "translate-x-1"
                    }`}
                  />
                </button>
                <span className="text-[0.65rem] uppercase tracking-[0.25em] text-[var(--text-muted)]">
                  {formData.enable_quick_reorder ? "ON" : "OFF"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Spacer for sticky footer */}
        <div className="h-24" />
      </div>

      {/* Sticky Save Bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 transition-all duration-300 ${
          hasUnsavedChanges || saving || success || saveError
            ? "translate-y-0"
            : "translate-y-full"
        }`}
      >
        <div className="bg-[var(--bg-elevated)] border-t border-theme shadow-lg backdrop-blur-sm">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-4">
            <div className="flex items-center justify-between gap-4">
              {/* Status indicator */}
              <div className="flex items-center gap-3">
                {hasUnsavedChanges && !saving && !success && !saveError && (
                  <div className="flex items-center gap-2 text-amber-500">
                    <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                    <span className="text-sm font-medium">Unsaved changes</span>
                  </div>
                )}
                {saving && (
                  <div className="flex items-center gap-2 text-[var(--accent)]">
                    <svg
                      className="w-5 h-5 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span className="text-sm font-medium">Saving...</span>
                  </div>
                )}
                {success && (
                  <div className="flex items-center gap-2 text-green-500">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-sm font-medium">
                      Settings saved successfully!
                    </span>
                  </div>
                )}
                {saveError && (
                  <div className="flex items-center gap-2 text-red-500">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    <span className="text-sm font-medium">{saveError}</span>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowResetConfirm(true)}
                  disabled={saving || !hasUnsavedChanges}
                  className="bg-theme-elevated text-[var(--text-main)] min-h-[44px] px-4 sm:px-6 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm border border-theme hover:bg-[var(--bg-main)]"
                >
                  Discard
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !hasUnsavedChanges}
                  className="bg-[var(--accent)] text-black min-h-[44px] px-6 sm:px-8 py-2.5 rounded-xl font-semibold hover:bg-[#B08D4F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center gap-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {saving ? "Saving..." : "Save All Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reset Confirmation Modal */}
      <ConfirmationModal
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={handleReset}
        title="Discard Changes"
        message="Are you sure you want to discard all unsaved changes? This action cannot be undone."
        confirmText="Discard"
        cancelText="Cancel"
        variant="warning"
      />
    </m.main>
  );
}

export default AdminFeatureFlags;
